import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthShell } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/context/AuthContext";
import { useApiError } from "@/lib/useApiError";

const Login = () => {
  const { t } = useTranslation();
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toMessage = useApiError();

  const [email, setEmail] = useState("");
  const [wachtwoord, setWachtwoord] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const redirectTo = location.state?.from || "/producten";

  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo, { replace: true });
  }, [isAuthenticated, navigate, redirectTo]);

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const account = await login(email.trim(), wachtwoord);
      toast.success(
        t("auth.login.success", {
          naam: account?.voornaam || account?.bedrijfsnaam || account?.email || "",
        }),
      );
      navigate(redirectTo, { replace: true });
    } catch (err) {
      // 403 = pending/rejected account → waiting page (briefing §6.2 / §8)
      if (err?.status === 403) {
        navigate("/wachten", { replace: true, state: { detail: err.detail } });
        return;
      }
      setError(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={t("auth.login.title")} subtitle={t("auth.login.subtitle")} testId="login-page">
        <form onSubmit={onSubmit} className="space-y-5">
          {error && (
            <Alert variant="destructive" data-testid="login-error-alert">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="login-email">{t("auth.login.fields.email")}</Label>
            <Input
              id="login-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-form-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="login-password">{t("auth.login.fields.wachtwoord")}</Label>
            <Input
              id="login-password"
              type="password"
              autoComplete="current-password"
              required
              value={wachtwoord}
              onChange={(e) => setWachtwoord(e.target.value)}
              data-testid="login-form-password-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={submitting}
            data-testid="login-form-submit-button"
          >
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <LogIn className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {submitting ? t("auth.login.submitting") : t("auth.login.submit")}
          </Button>

          <p className="text-center text-sm">
            <Link
              to="/wachtwoord-vergeten"
              className="font-medium text-primary hover:underline"
              data-testid="login-forgot-password-link"
            >
              {t("auth.login.forgot")}
            </Link>
          </p>

          <p className="text-center text-sm text-muted-foreground">
            {t("auth.login.noAccount")}{" "}
            <Link to="/registreren" className="font-medium text-primary hover:underline" data-testid="login-register-link">
              {t("nav.register")}
            </Link>
          </p>
        </form>
    </AuthShell>
  );
};

export default Login;
